import { MIC_ERR_MEDIA_STREAM } from "../../../constants/microphone";
import { MicError } from "../../../models/microphone";

export const STREAM_WINDOW_KEY = "jz-mic-stream";
export const STREAM_ERR_NO_MEDIA_DEVICES = "No media devices found";

/**
 * Extending the default definition with browser specific definitions for backward compatibility
 * https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common.browser/MicAudioSource.ts#L39
 */
interface INavigator extends Navigator {
  webkitGetUserMedia?: (
    constraints: MediaStreamConstraints,
    successCallback: NavigatorUserMediaSuccessCallback,
    errorCallback: NavigatorUserMediaErrorCallback
  ) => void;
  mozGetUserMedia?: (
    constraints: MediaStreamConstraints,
    successCallback: NavigatorUserMediaSuccessCallback,
    errorCallback: NavigatorUserMediaErrorCallback
  ) => void;
  msGetUserMedia?: (
    constraints: MediaStreamConstraints,
    successCallback: NavigatorUserMediaSuccessCallback,
    errorCallback: NavigatorUserMediaErrorCallback
  ) => void;
}

/**
 *
 * @param stream to be cleared MediaStream
 * @see https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common.browser/PCMRecorder.ts#L102
 */
export const clearMediaStream = (stream?: MediaStream): undefined => {
  if (stream) {
    stream.getTracks().forEach((track: any) => track.stop());
  }
  return;
};

/**
 * Mainly copied from Microsoft SDK source for backward compatibility
 * @see Exceptions https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#Exceptions
 * @see Source https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common.browser/MicAudioSource.ts#L102
 * @param audioConstraints
 */
export const getMediaStream = async (
  audioConstraints: MediaTrackConstraints = { channelCount: 1 }
): Promise<MediaStream> => {
  let getUserMedia: (
    constraints: MediaStreamConstraints,
    successCallback: NavigatorUserMediaSuccessCallback,
    errorCallback: NavigatorUserMediaErrorCallback
  ) => void;
  const nav = window.navigator as INavigator;

  // Vendor prefix
  getUserMedia =
    nav.getUserMedia ||
    nav.webkitGetUserMedia ||
    nav.mozGetUserMedia ||
    nav.msGetUserMedia;

  // The best way
  if (!!nav.mediaDevices) {
    getUserMedia = (
      constraints: MediaStreamConstraints,
      successCallback: NavigatorUserMediaSuccessCallback,
      errorCallback: NavigatorUserMediaErrorCallback
    ): void => {
      nav.mediaDevices
        .getUserMedia(constraints)
        .then(successCallback)
        .catch(errorCallback);
    };
  }

  // Erf
  if (!getUserMedia) {
    throw new Error(STREAM_ERR_NO_MEDIA_DEVICES);
  }

  // https://stackoverflow.com/a/22519785/4906586
  return new Promise((resolve, reject) => {
    getUserMedia(
      { audio: audioConstraints, video: false },
      (mediaStream) => resolve(mediaStream),
      (err: MediaStreamError) => reject(err)
    );
  });
};

/**
 *
 * @param err to be checked error
 * @returns A MicError if the `err` parameter is a MediaStream error, or
 * undefined otherwise
 */
export const getMediaStreamError = (err: any): MicError | undefined => {
  const errAsMediaStreamError = err as MediaStreamError;

  const mediaStreamErrors: MicError[] = [
    MIC_ERR_MEDIA_STREAM.ABORT,
    MIC_ERR_MEDIA_STREAM.NOT_ALLOWED,
    MIC_ERR_MEDIA_STREAM.NOT_FOUND,
    MIC_ERR_MEDIA_STREAM.NOT_READABLE,
    MIC_ERR_MEDIA_STREAM.OVERCONSTRAINT,
    MIC_ERR_MEDIA_STREAM.SECURITY,
    MIC_ERR_MEDIA_STREAM.TYPE,
  ];

  for (const e of mediaStreamErrors) {
    if (errAsMediaStreamError.name === e.value) {
      return {
        ...e,
        // Cannot use spread operator here
        details: {
          name: errAsMediaStreamError.name,
          message: errAsMediaStreamError.message,
          constraintName: errAsMediaStreamError.constraintName,
        },
      };
    }
  }

  return undefined;
};
